import { Icon } from "materia"
import { useTranslation } from "react-i18next"
import type { PeerProfile } from "../../types"
import { ChatModal } from "../ChatModal"
import { PeerAvatar } from "../PeerAvatar"
import s from "./ManageMembers.module.scss"

/** The handle is what add, remove, and promote/demote are addressed by. */
export type GroupMember = PeerProfile & {
  handle: string
  isOwner: boolean
  isAdmin: boolean
  /** Your own row — leaving is the menu's "Leave group", not a removal. */
  isSelf: boolean
}

/** A plain member only looks; an owner or admin removes, or changes admins. */
export type MemberListMode = "view" | "remove" | "admins"

const TITLES: Record<MemberListMode, string> = {
  view: "chat.MEMBERS",
  remove: "chat.MANAGE_MEMBERS",
  admins: "chat.MANAGE_ADMINS",
}

type ManageMembersProps = {
  mode: MemberListMode
  members: GroupMember[]
  onRemove: (member: GroupMember) => void
  onPromote: (member: GroupMember) => void
  onDemote: (member: GroupMember) => void
  onClose: () => void
}

export function ManageMembers({
  mode,
  members,
  onRemove,
  onPromote,
  onDemote,
  onClose,
}: ManageMembersProps) {
  const { t } = useTranslation()
  const title = String(t(TITLES[mode]))

  return (
    <ChatModal ariaLabel={title} onClose={onClose}>
      <div className={s.members}>
        <h2 className={s.members__title}>{title}</h2>

        <div className={s.members__list}>
          {members.map((member) => (
            <div className={s.members__member} key={member.handle}>
              <span className={s.members__avatar}>
                <PeerAvatar {...member} size="medium" />
              </span>
              <span className={s.members__name}>
                {member.displayName}
                {(member.isOwner || member.isAdmin) && (
                  <span className={s.members__role}>
                    {t(
                      member.isOwner ? "chat.GROUP_OWNER" : "chat.GROUP_ADMIN"
                    )}
                  </span>
                )}
              </span>

              {/* Never on the owner's row — the owner cannot be demoted. Shown
                  on your own row so an admin can step down (FAME-574). */}
              {mode === "admins" && !member.isOwner && (
                <button
                  className={s.members__roleButton}
                  type="button"
                  onClick={() =>
                    member.isAdmin ? onDemote(member) : onPromote(member)
                  }
                >
                  {t(
                    member.isAdmin ? "chat.DEMOTE_ADMIN" : "chat.PROMOTE_ADMIN"
                  )}
                </button>
              )}

              {mode === "remove" && !member.isOwner && !member.isSelf && (
                <button
                  className={s.members__remove}
                  type="button"
                  onClick={() => onRemove(member)}
                  aria-label={String(
                    t("chat.REMOVE_MEMBER_ARIA", { name: member.displayName })
                  )}
                >
                  <Icon icon="Close" size="small" />
                </button>
              )}
            </div>
          ))}
        </div>

        {mode === "view" && (
          <p className={s.members__note}>{t("chat.OWNER_ONLY")}</p>
        )}
      </div>
    </ChatModal>
  )
}
